import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { dashboardAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { FiRefreshCw, FiPlus, FiEdit2, FiTrash2, FiMove, FiFolderPlus } from 'react-icons/fi';
import KpiGroup from './KpiGroup';
import KpiLineChart from './KpiLineChart';
import KpiBarChart from './KpiBarChart';
import KpiAreaChart from './KpiAreaChart';
import KpiPieChart from './KpiPieChart';

const Dashboard = () => {
  const { user } = useAuth();
  const [kpis, setKpis] = useState([]);
  const [groups, setGroups] = useState([]);
  const [dataGrids, setDataGrids] = useState([]);
  const [actions, setActions] = useState([]);
  const [availableKpis, setAvailableKpis] = useState([]);
  const [availableDataGrids, setAvailableDataGrids] = useState([]);
  const [availableActions, setAvailableActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddKpi, setShowAddKpi] = useState(false);
  const [showAddDataGrid, setShowAddDataGrid] = useState(false);
  const [showAddAction, setShowAddAction] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [editingKpi, setEditingKpi] = useState(null);
  const [editingDataGrid, setEditingDataGrid] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  // Load Zapier chatbot script for authenticated users
  useEffect(() => {
    if (user) {
      // Check if script is already loaded
      const existingScript = document.querySelector('script[src*="zapier-interfaces"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.async = true;
        script.type = 'module';
        script.src = 'https://interfaces.zapier.com/assets/web-components/zapier-interfaces/zapier-interfaces.esm.js';
        document.head.appendChild(script);

        // Cleanup function to remove script when component unmounts
        return () => {
          const scriptToRemove = document.querySelector('script[src*="zapier-interfaces"]');
          if (scriptToRemove) {
            document.head.removeChild(scriptToRemove);
          }
        };
      }
    }
  }, [user]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [kpiDataResponse, groupsResponse, availableKpisResponse, dataGridDataResponse, availableDataGridsResponse, actionsResponse, availableActionsResponse] = await Promise.all([
        dashboardAPI.getAllKpiData(),
        dashboardAPI.getGroups(),
        dashboardAPI.getAvailableKpis(),
        dashboardAPI.getAllDataGridData(),
        dashboardAPI.getAvailableDataGrids(),
        dashboardAPI.getMyActions(),
        dashboardAPI.getAvailableActions()
      ]);
      setKpis(kpiDataResponse.data.kpis || []);
      setGroups(groupsResponse.data.groups || []);
      setAvailableKpis(availableKpisResponse.data.kpis || []);
      setDataGrids(dataGridDataResponse.data.dataGrids || []);
      setAvailableDataGrids(availableDataGridsResponse.data.dataGrids || []);
      setActions(actionsResponse.data.actions || []);
      setAvailableActions(availableActionsResponse.data.actions || []);
      setError('');
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddKpi = async (kpiConfigId, customName, groupName = 'Default') => {
    try {
      await dashboardAPI.addKpi(kpiConfigId, 0, 0, 'medium', customName, groupName);
      // Reload dashboard to show the new KPI
      loadDashboard();
      setShowAddKpi(false);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add KPI to dashboard');
    }
  };

  const handleDeleteKpi = async (kpiId) => {
    if (!window.confirm('Are you sure you want to remove this KPI from your dashboard?')) {
      return;
    }

    try {
      await dashboardAPI.removeKpi(kpiId);
      // Reload dashboard to remove the KPI
      loadDashboard();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to remove KPI from dashboard');
    }
  };

  const handleEditKpi = (kpi) => {
    setEditingKpi(kpi);
  };

  const handleUpdateKpiName = async (kpiId, newName) => {
    try {
      await dashboardAPI.updateKpiName(kpiId, newName);
      // Reload dashboard to show the updated name
      loadDashboard();
      setEditingKpi(null);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update KPI name');
    }
  };

  // Data Grid handlers
  const handleAddDataGrid = async (dataGridConfigId, customName) => {
    try {
      await dashboardAPI.addDataGrid(dataGridConfigId, 0, 0, 'large', customName);
      // Reload dashboard to show the new Data Grid
      loadDashboard();
      setShowAddDataGrid(false);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add Data Grid to dashboard');
    }
  };

  // Action handlers
  const handleAddAction = async (actionConfigId, customName) => {
    try {
      await dashboardAPI.addAction(actionConfigId, customName);
      // Reload dashboard to show the new Action
      loadDashboard();
      setShowAddAction(false);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add Action to dashboard');
    }
  };

  const handleDeleteDataGrid = async (dataGridId) => {
    if (!window.confirm('Are you sure you want to remove this Data Grid from your dashboard?')) {
      return;
    }

    try {
      await dashboardAPI.removeDataGrid(dataGridId);
      // Reload dashboard to remove the Data Grid
      loadDashboard();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to remove Data Grid from dashboard');
    }
  };

  const handleEditDataGrid = (dataGrid) => {
    setEditingDataGrid(dataGrid);
  };

  const handleUpdateDataGridName = async (dataGridId, newName) => {
    try {
      await dashboardAPI.updateDataGridName(dataGridId, newName);
      // Reload dashboard to show the updated name
      loadDashboard();
      setEditingDataGrid(null);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update Data Grid name');
    }
  };

  // Group KPIs by their groupName
  const groupedKpis = useMemo(() => {
    const grouped = {};
    
    // Initialize all groups (including those from database)
    groups.forEach(group => {
      grouped[group.name] = [];
    });
    
    // Always ensure Default group exists
    if (!grouped['Default']) {
      grouped['Default'] = [];
    }
    
    // Group KPIs
    kpis.forEach(kpi => {
      const groupName = kpi.groupName || 'Default';
      if (!grouped[groupName]) {
        grouped[groupName] = [];
      }
      grouped[groupName].push(kpi);
    });
    
    return grouped;
  }, [kpis, groups]);

  const moveKpi = useCallback((dragIndex, hoverIndex, sourceGroupName, targetGroupName) => {
    // Only handle reordering within the same group here
    // Moving between groups is handled by the KpiGroup drop handler
    if (sourceGroupName === targetGroupName) {
      const groupKpis = groupedKpis[sourceGroupName] || [];
      if (dragIndex === hoverIndex || !groupKpis[dragIndex] || !groupKpis[hoverIndex]) {
        return;
      }
      
      const dragKpi = groupKpis[dragIndex];
      const newKpis = [...kpis];
      
      // Find the actual indices in the full kpis array
      const dragKpiIndex = newKpis.findIndex(k => k.kpiId === dragKpi.kpiId);
      const hoverKpi = groupKpis[hoverIndex];
      const hoverKpiIndex = newKpis.findIndex(k => k.kpiId === hoverKpi.kpiId);
      
      if (dragKpiIndex !== -1 && hoverKpiIndex !== -1) {
        newKpis.splice(dragKpiIndex, 1);
        newKpis.splice(hoverKpiIndex, 0, dragKpi);
        setKpis(newKpis);
      }
    }
  }, [kpis, groupedKpis]);

  const saveKpiOrder = useCallback(async () => {
    try {
      // Update positions in the backend
      const updates = kpis.map((kpi, index) => ({
        kpiId: kpi.kpiId,
        position: index
      }));
      
      // Save the new order
      await dashboardAPI.saveLayout({ kpis: updates });
    } catch (error) {
      console.error('Failed to save KPI order:', error);
      setError('Failed to save KPI order');
    }
  }, [kpis]);

  // Data Grid drag and drop handlers
  const moveDataGrid = useCallback((dragIndex, hoverIndex) => {
    if (dragIndex === hoverIndex || !dataGrids[dragIndex] || !dataGrids[hoverIndex]) {
      return;
    }
    
    const dragDataGrid = dataGrids[dragIndex];
    const newDataGrids = [...dataGrids];
    
    newDataGrids.splice(dragIndex, 1);
    newDataGrids.splice(hoverIndex, 0, dragDataGrid);
    setDataGrids(newDataGrids);
  }, [dataGrids]);

  const saveDataGridOrder = useCallback(async () => {
    try {
      // Update display order in the backend
      const updates = dataGrids.map((dataGrid, index) => ({
        dataGridId: dataGrid.dataGridId,
        displayOrder: index
      }));
      
      // Save the new order
      await dashboardAPI.saveDataGridOrder(updates);
    } catch (error) {
      console.error('Failed to save data grid order:', error);
      setError('Failed to save data grid order');
    }
  }, [dataGrids]);

  const refreshData = async () => {
    try {
      setRefreshing(true);
      // Force fresh API calls by skipping cache
      const [kpiResponse, dataGridResponse] = await Promise.all([
        dashboardAPI.getAllKpiData(true), // skipCache = true
        dashboardAPI.getAllDataGridData(true) // skipCache = true
      ]);
      setKpis(kpiResponse.data.kpis || []);
      setDataGrids(dataGridResponse.data.dataGrids || []);
      setError('');
    } catch (err) {
      console.error('Failed to refresh data:', err);
      setError('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  // Group management handlers
  const handleCreateGroup = async (groupName) => {
    try {
      await dashboardAPI.createGroup(groupName);
      loadDashboard();
      setShowCreateGroup(false);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create group');
    }
  };

  const handleToggleGroupCollapse = async (groupName, isCollapsed) => {
    try {
      const group = groups.find(g => g.name === groupName);
      if (group) {
        await dashboardAPI.updateGroupCollapseState(group.id, isCollapsed);
        loadDashboard();
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update group collapse state');
    }
  };

  const handleEditGroup = (groupName) => {
    setEditingGroup(groupName);
  };

  const handleUpdateGroupName = async (oldName, newName) => {
    try {
      const group = groups.find(g => g.name === oldName);
      if (group) {
        await dashboardAPI.updateGroupName(group.id, newName);
        loadDashboard();
        setEditingGroup(null);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update group name');
    }
  };

  const handleDeleteGroup = async (groupName) => {
    try {
      const group = groups.find(g => g.name === groupName);
      if (group) {
        await dashboardAPI.deleteGroup(group.id);
        loadDashboard();
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete group');
    }
  };

  const handleMoveKpiToGroup = async (kpiId, targetGroupName) => {
    try {
      await dashboardAPI.updateKpiGroup(kpiId, targetGroupName);
      loadDashboard();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to move KPI to group');
    }
  };

  const renderKpiGroups = () => {
    const groupNames = Object.keys(groupedKpis).sort((a, b) => {
      // Default group always first
      if (a === 'Default') return -1;
      if (b === 'Default') return 1;
      
      // Sort other groups by their order from database
      const groupA = groups.find(g => g.name === a);
      const groupB = groups.find(g => g.name === b);
      
      if (groupA && groupB) {
        return groupA.groupOrder - groupB.groupOrder;
      }
      
      return a.localeCompare(b);
    });

    return groupNames.map(groupName => {
      const groupKpis = groupedKpis[groupName];
      const group = groups.find(g => g.name === groupName);
      const isCollapsed = group ? group.isCollapsed : false;

      return (
        <KpiGroup
          key={groupName}
          groupName={groupName}
          kpis={groupKpis}
          isCollapsed={isCollapsed}
          onToggleCollapse={handleToggleGroupCollapse}
          onEditGroup={handleEditGroup}
          onDeleteGroup={handleDeleteGroup}
          onMoveKpiToGroup={handleMoveKpiToGroup}
          onEditKpi={handleEditKpi}
          onDeleteKpi={handleDeleteKpi}
          DraggableKpiCard={DraggableKpiCard}
          moveKpi={moveKpi}
          saveKpiOrder={saveKpiOrder}
          canDelete={groupName !== 'Default'}
        />
      );
    });
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="container">
        {/* Dashboard Header */}
        <div className="dashboard-header fade-in">
          <div className="dashboard-title">Dashboard</div>
          <div className="dashboard-subtitle">
            Welcome back, {user?.email}! Monitor your key metrics and data insights.
          </div>
          <div className="dashboard-actions">
            {user?.hasApiCredentials && availableKpis.length > 0 && (
              <button
                onClick={() => setShowAddKpi(true)}
                className="btn btn-primary"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <FiPlus style={{ marginRight: '8px' }} />
                Add KPI
              </button>
            )}
            {user?.hasApiCredentials && availableDataGrids.length > 0 && (
              <button
                onClick={() => setShowAddDataGrid(true)}
                className="btn btn-primary"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <FiPlus style={{ marginRight: '8px' }} />
                Add Data Grid
              </button>
            )}
            {user?.hasApiCredentials && availableActions.length > 0 && (
              <button
                onClick={() => setShowAddAction(true)}
                className="btn btn-primary"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <FiPlus style={{ marginRight: '8px' }} />
                Add Action
              </button>
            )}
            <button
              onClick={refreshData}
              disabled={refreshing}
              className="btn btn-secondary"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <FiRefreshCw style={{ marginRight: '8px', animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div className="card" style={{ backgroundColor: '#f8d7da', borderColor: '#f5c6cb', color: '#721c24' }}>
            {error}
          </div>
        )}

        {!user?.hasApiCredentials && (
          <div className="card" style={{ backgroundColor: '#fff3cd', borderColor: '#ffeaa7', color: '#856404' }}>
            <h3>Setup Required</h3>
            <p>
              To start using your dashboard, you need to configure your API credentials in the{' '}
              <a href="/settings" style={{ color: '#856404' }}>Settings</a> page.
            </p>
          </div>
        )}

        {/* KPI Section */}
        {kpis.length > 0 && (
          <div style={{ marginBottom: '40px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: 'var(--text-primary)' }}>KPI Cards</h2>
              <button
                onClick={() => setShowCreateGroup(true)}
                className="btn btn-secondary"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <FiFolderPlus style={{ marginRight: '8px' }} />
                Create Group
              </button>
            </div>
            {renderKpiGroups()}
          </div>
        )}

        {/* Data Grid Section */}
        {dataGrids.length > 0 && (
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>Data Grids</h2>
            <div className="dashboard-grid">
              {dataGrids.map((dataGrid, index) => (
                <DraggableDataGridCard 
                  key={dataGrid.dataGridId} 
                  dataGrid={dataGrid} 
                  index={index}
                  moveDataGrid={moveDataGrid}
                  onEdit={handleEditDataGrid}
                  onDelete={handleDeleteDataGrid}
                  onDragEnd={saveDataGridOrder}
                />
              ))}
            </div>
          </div>
        )}

        {/* Actions Section */}
        {actions.length > 0 && (
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>Actions</h2>
            <div className="actions-grid" style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '20px' 
            }}>
              {actions.map((action) => (
                <ActionCard 
                  key={action.id} 
                  action={action}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {kpis.length === 0 && dataGrids.length === 0 && actions.length === 0 && (
          <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <h3>Dashboard Empty</h3>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Your dashboard is empty. Add some KPI cards or Data Grids to get started!
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {user?.hasApiCredentials && availableKpis.length > 0 && (
                <button
                  onClick={() => setShowAddKpi(true)}
                  className="btn btn-primary"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <FiPlus style={{ marginRight: '5px' }} />
                  Add Your First KPI
                </button>
              )}
              {user?.hasApiCredentials && availableDataGrids.length > 0 && (
                <button
                  onClick={() => setShowAddDataGrid(true)}
                  className="btn btn-primary"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <FiPlus style={{ marginRight: '5px' }} />
                  Add Your First Data Grid
                </button>
              )}
            </div>
            {user?.hasApiCredentials && availableKpis.length === 0 && availableDataGrids.length === 0 && (
              <p style={{ color: '#666', marginTop: '15px' }}>
                No configurations available. Contact your administrator to configure dashboard options.
              </p>
            )}
          </div>
        )}

        {showAddKpi && (
          <AddKpiModal
            availableKpis={availableKpis}
            userKpis={kpis}
            groups={groups}
            onClose={() => setShowAddKpi(false)}
            onAddKpi={handleAddKpi}
          />
        )}

        {showAddDataGrid && (
          <AddDataGridModal
            availableDataGrids={availableDataGrids}
            userDataGrids={dataGrids}
            onClose={() => setShowAddDataGrid(false)}
            onAddDataGrid={handleAddDataGrid}
          />
        )}

        {showAddAction && (
          <AddActionModal
            availableActions={availableActions}
            userActions={actions}
            onClose={() => setShowAddAction(false)}
            onAddAction={handleAddAction}
          />
        )}

        {editingKpi && (
          <EditKpiModal
            kpi={editingKpi}
            onClose={() => setEditingKpi(null)}
            onUpdate={handleUpdateKpiName}
          />
        )}

        {editingDataGrid && (
          <EditDataGridModal
            dataGrid={editingDataGrid}
            onClose={() => setEditingDataGrid(null)}
            onUpdate={handleUpdateDataGridName}
          />
        )}

        {showCreateGroup && (
          <CreateGroupModal
            onClose={() => setShowCreateGroup(false)}
            onCreate={handleCreateGroup}
          />
        )}

        {editingGroup && (
          <EditGroupModal
            groupName={editingGroup}
            onClose={() => setEditingGroup(null)}
            onUpdate={handleUpdateGroupName}
          />
        )}

        {/* Zapier Chatbot - Only show for authenticated users */}
        {user && (
          <zapier-interfaces-chatbot-embed 
            is-popup="true" 
            chatbot-id="cme8iqdds0024f4ghbgcuu7f8"
          />
        )}
      </div>
    </DndProvider>
  );
};


const AddKpiModal = ({ availableKpis, userKpis, groups, onClose, onAddKpi }) => {
  const [selectedKpi, setSelectedKpi] = useState('');
  const [customName, setCustomName] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('Default');
  const [adding, setAdding] = useState(false);

  // Filter out KPIs that are already added to the dashboard
  const userKpiConfigIds = userKpis.map(kpi => kpi.kpiConfigId);
  const availableToAdd = availableKpis.filter(kpi => !userKpiConfigIds.includes(kpi.id));

  // Prepare group options (Default + custom groups)
  const groupOptions = useMemo(() => {
    const options = [];
    
    // Always add Default group first
    options.push({ name: 'Default', id: 'default' });
    
    // Add all other groups from the database
    if (groups && groups.length > 0) {
      groups.forEach(group => {
        if (group.name !== 'Default') {
          options.push({ name: group.name, id: group.id });
        }
      });
    }
    
    return options;
  }, [groups]);

  // Update custom name when KPI selection changes
  const handleKpiSelection = (kpiId) => {
    setSelectedKpi(kpiId);
    if (kpiId) {
      const kpi = availableToAdd.find(k => k.id.toString() === kpiId);
      setCustomName(kpi ? kpi.name : '');
    } else {
      setCustomName('');
    }
  };

  const handleAdd = async () => {
    if (!selectedKpi || !customName.trim()) return;

    setAdding(true);
    try {
      await onAddKpi(parseInt(selectedKpi), customName.trim(), selectedGroup);
    } catch (error) {
      console.error('Failed to add KPI:', error);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>Add KPI to Dashboard</h3>
          <button onClick={onClose} className="btn btn-small btn-secondary">×</button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <p style={{ color: '#666', marginBottom: '15px' }}>
            Select a KPI configuration to add to your dashboard:
          </p>

          {availableToAdd.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              <p>All available KPIs have been added to your dashboard.</p>
            </div>
          ) : (
            <div>
              <div className="form-group">
                <label className="form-label">Available KPIs</label>
                <select
                  value={selectedKpi}
                  onChange={(e) => handleKpiSelection(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select a KPI to add</option>
                  {availableToAdd.map(kpi => (
                    <option key={kpi.id} value={kpi.id}>
                      {kpi.name} ({kpi.displayType}) - {kpi.endpointName}
                    </option>
                  ))}
                </select>
              </div>

              {selectedKpi && (
                <div className="form-group">
                  <label className="form-label">Group</label>
                  <select
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    className="form-input"
                  >
                    {groupOptions.map(group => (
                      <option key={group.id} value={group.name}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Choose which group to add this KPI to
                  </small>
                </div>
              )}

              {selectedKpi && (
                <>
                  <div className="form-group">
                    <label className="form-label">Custom Name</label>
                    <input
                      type="text"
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                      className="form-input"
                      placeholder="Enter a custom name for this KPI"
                      required
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Customize the display name for this KPI on your dashboard
                    </small>
                  </div>
                </>
              )}

              {selectedKpi && (
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #dee2e6', 
                  borderRadius: '5px', 
                  padding: '15px',
                  marginBottom: '15px'
                }}>
                  {(() => {
                    const kpi = availableToAdd.find(k => k.id.toString() === selectedKpi);
                    return kpi ? (
                      <div>
                        <h5 style={{ margin: '0 0 10px 0', color: '#333' }}>{kpi.name}</h5>
                        <p style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>
                          {kpi.description}
                        </p>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          <strong>Display Type:</strong> {kpi.displayType.replace('_', ' ').toUpperCase()}<br/>
                          <strong>Data Source:</strong> {kpi.endpointName}<br/>
                          {kpi.units && <><strong>Units:</strong> {kpi.units}</>}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          {availableToAdd.length > 0 && (
            <button 
              onClick={handleAdd} 
              disabled={!selectedKpi || adding}
              className="btn btn-primary"
            >
              {adding ? 'Adding...' : 'Add KPI'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const EditKpiModal = ({ kpi, onClose, onUpdate }) => {
  const [customName, setCustomName] = useState(kpi.name || '');
  const [updating, setUpdating] = useState(false);

  const handleUpdate = async () => {
    if (!customName.trim()) return;

    setUpdating(true);
    try {
      await onUpdate(kpi.kpiId, customName.trim());
    } catch (error) {
      console.error('Failed to update KPI name:', error);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '400px' }}>
        <div className="modal-header">
          <h3>Edit KPI Name</h3>
          <button onClick={onClose} className="btn btn-small btn-secondary">×</button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <div className="form-group">
            <label className="form-label">KPI Name</label>
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              className="form-input"
              placeholder="Enter KPI name"
              required
              autoFocus
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              This name will be displayed on your dashboard
            </small>
          </div>

          <div style={{ 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #dee2e6', 
            borderRadius: '5px', 
            padding: '15px',
            marginTop: '15px'
          }}>
            <div style={{ fontSize: '12px', color: '#666' }}>
              <strong>Display Type:</strong> {kpi.displayType?.replace('_', ' ').toUpperCase()}<br/>
              {kpi.units && <><strong>Units:</strong> {kpi.units}<br/></>}
              <strong>Current Value:</strong> {kpi.value !== undefined ? kpi.value : 'Loading...'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button 
            onClick={handleUpdate} 
            disabled={!customName.trim() || updating}
            className="btn btn-primary"
          >
            {updating ? 'Updating...' : 'Update Name'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DraggableKpiCard = ({ kpi, index, moveKpi, onEdit, onDelete, onDragEnd, groupName }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'kpi',
    item: () => {
      return { index, kpiId: kpi.kpiId, groupName };
    },
    end: (item, monitor) => {
      onDragEnd();
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'kpi',
    hover: (item, monitor) => {
      if (!monitor.isOver({ shallow: true })) {
        return;
      }
      
      // Only handle reordering within the same group
      if (item.groupName === groupName && item.index !== index) {
        moveKpi(item.index, index, item.groupName, groupName);
        item.index = index;
      }
    },
  });

  // Check if this is a chart type KPI
  const isChartType = ['line_chart', 'bar_chart', 'area_chart', 'pie_chart'].includes(kpi.displayType);

  const formatValue = (value, units) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    
    if (typeof value === 'number') {
      return (units ? `${units} ` : '') + value.toLocaleString();
    }
    
    return (units ? `${units} ` : '') + value.toString();
  };

  const getValueColor = (value, thresholds) => {
    if (!thresholds || typeof value !== 'number') {
      return '#007bff';
    }

    if (thresholds.danger && value >= thresholds.danger) {
      return '#dc3545';
    }
    if (thresholds.warning && value >= thresholds.warning) {
      return '#ffc107';
    }
    if (thresholds.success && value >= thresholds.success) {
      return '#28a745';
    }
    
    return '#007bff';
  };

  return (
    <div 
      ref={(node) => drag(drop(node))}
      className={`kpi-card ${isChartType ? 'chart-type' : ''} ${isDragging ? 'dragging' : ''}`}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        transform: isDragging ? 'rotate(5deg)' : 'none',
        transition: 'transform 0.2s ease'
      }}
    >
      <div className="kpi-card-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FiMove style={{ marginRight: '8px', color: '#666', cursor: 'grab' }} size={14} />
          <h3 className="kpi-card-title" style={{ margin: 0 }}>{kpi.name}</h3>
        </div>
        <div className="kpi-card-actions">
          <button
            onClick={() => onEdit(kpi)}
            className="btn btn-small btn-secondary"
            title="Edit KPI Name"
            style={{ padding: '4px 8px', marginRight: '5px' }}
          >
            <FiEdit2 size={12} />
          </button>
          <button
            onClick={() => onDelete(kpi.kpiId)}
            className="btn btn-small btn-danger"
            title="Remove KPI"
            style={{ padding: '4px 8px' }}
          >
            <FiTrash2 size={12} />
          </button>
        </div>
      </div>
      
      {kpi.error ? (
        <div style={{ color: '#dc3545', padding: '20px', textAlign: 'center' }}>
          <p>Error loading data</p>
          <small>{kpi.error}</small>
        </div>
      ) : (
        <div>
          {kpi.displayType === 'number' && (
            <div style={{ textAlign: 'center' }}>
              <div 
                className="kpi-card-value"
                style={{ color: getValueColor(kpi.value, kpi.thresholds) }}
              >
                {formatValue(kpi.value, kpi.units)}
              </div>
            </div>
          )}
          
          {kpi.displayType === 'progress' && (
            <div style={{ textAlign: 'center' }}>
              <div className="kpi-card-value">
                {formatValue(kpi.value, kpi.units)}
              </div>
              <div style={{ marginTop: '10px' }}>
                <div style={{
                  width: '100%',
                  height: '10px',
                  backgroundColor: '#e9ecef',
                  borderRadius: '5px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${Math.min(100, Math.max(0, (kpi.value / 100) * 100))}%`,
                    height: '100%',
                    backgroundColor: getValueColor(kpi.value, kpi.thresholds),
                    transition: 'width 0.3s ease'
                  }}></div>
                </div>
              </div>
            </div>
          )}
          
          {kpi.displayType === 'line_chart' && (
            <div>
              {kpi.chartError ? (
                <div style={{ color: '#dc3545', padding: '20px', textAlign: 'center' }}>
                  <p>Chart Error</p>
                  <small>{kpi.chartError}</small>
                </div>
              ) : kpi.chartData ? (
                <KpiLineChart 
                  data={kpi.chartData} 
                  title={kpi.name} 
                  color={kpi.chartColor || '#007bff'} 
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  <small>Loading chart data...</small>
                </div>
              )}
            </div>
          )}
          
          {kpi.displayType === 'bar_chart' && (
            <div>
              {kpi.chartError ? (
                <div style={{ color: '#dc3545', padding: '20px', textAlign: 'center' }}>
                  <p>Chart Error</p>
                  <small>{kpi.chartError}</small>
                </div>
              ) : kpi.chartData ? (
                <KpiBarChart 
                  data={kpi.chartData} 
                  title={kpi.name} 
                  color={kpi.chartColor || '#007bff'} 
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  <small>Loading chart data...</small>
                </div>
              )}
            </div>
          )}
          
          {kpi.displayType === 'area_chart' && (
            <div>
              {kpi.chartError ? (
                <div style={{ color: '#dc3545', padding: '20px', textAlign: 'center' }}>
                  <p>Chart Error</p>
                  <small>{kpi.chartError}</small>
                </div>
              ) : kpi.chartData ? (
                <KpiAreaChart 
                  data={kpi.chartData} 
                  title={kpi.name} 
                  color={kpi.chartColor || '#007bff'} 
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  <small>Loading chart data...</small>
                </div>
              )}
            </div>
          )}
          
          {kpi.displayType === 'pie_chart' && (
            <div>
              {kpi.chartError ? (
                <div style={{ color: '#dc3545', padding: '20px', textAlign: 'center' }}>
                  <p>Chart Error</p>
                  <small>{kpi.chartError}</small>
                </div>
              ) : kpi.chartData ? (
                <KpiPieChart 
                  data={kpi.chartData} 
                  title={kpi.name} 
                  color={kpi.chartColor || '#007bff'} 
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  <small>Loading chart data...</small>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      <div style={{ marginTop: '15px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
        Last updated: {new Date().toLocaleTimeString()}
      </div>
    </div>
  );
};

// Data Grid Components
const DraggableDataGridCard = ({ dataGrid, index, moveDataGrid, onEdit, onDelete, onDragEnd }) => {
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterText, setFilterText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [originalData, setOriginalData] = useState([]);

  const [{ isDragging }, drag] = useDrag({
    type: 'dataGrid',
    item: () => {
      return { index, dataGridId: dataGrid.dataGridId };
    },
    end: (item, monitor) => {
      onDragEnd();
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'dataGrid',
    hover: (item, monitor) => {
      if (!monitor.isOver({ shallow: true })) {
        return;
      }
      
      if (item.index !== index) {
        moveDataGrid(item.index, index);
        item.index = index;
      }
    },
  });

  // Process and prepare data
  useEffect(() => {
    let tableData = [];
    
    if (Array.isArray(dataGrid.data)) {
      tableData = dataGrid.data;
    } else if (dataGrid.data && typeof dataGrid.data === 'object') {
      // Handle different data formats - convert objects to arrays
      if (Array.isArray(dataGrid.data.data)) {
        tableData = dataGrid.data.data;
      } else if (Array.isArray(dataGrid.data.results)) {
        tableData = dataGrid.data.results;
      } else if (Array.isArray(dataGrid.data.items)) {
        tableData = dataGrid.data.items;
      } else if (Array.isArray(dataGrid.data.rows)) {
        tableData = dataGrid.data.rows;
      } else if (Array.isArray(dataGrid.data.records)) {
        tableData = dataGrid.data.records;
      } else {
        // Convert object to array format
        const keys = Object.keys(dataGrid.data);
        if (keys.length > 0) {
          const firstValue = dataGrid.data[keys[0]];
          if (typeof firstValue === 'string' || typeof firstValue === 'number' || firstValue === null) {
            // Single record object
            tableData = [dataGrid.data];
          } else {
            // Multiple records as object properties
            tableData = keys.map(key => ({ key, ...dataGrid.data[key] }));
          }
        }
      }
    }
    
    setOriginalData(tableData);
    setFilteredData(tableData);
  }, [dataGrid.data]);

  // Apply filtering
  useEffect(() => {
    if (!filterText.trim()) {
      setFilteredData(originalData);
      return;
    }

    const filtered = originalData.filter(row => {
      return Object.values(row).some(value => {
        if (value === null || value === undefined) return false;
        return value.toString().toLowerCase().includes(filterText.toLowerCase());
      });
    });
    
    setFilteredData(filtered);
  }, [filterText, originalData]);

  // Apply sorting
  const sortedData = useMemo(() => {
    if (!sortField) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      
      // Handle null/undefined values
      if (aVal === null || aVal === undefined) return sortDirection === 'asc' ? 1 : -1;
      if (bVal === null || bVal === undefined) return sortDirection === 'asc' ? -1 : 1;
      
      // Handle numeric values
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }
      
      // Handle string values
      const aStr = aVal.toString().toLowerCase();
      const bStr = bVal.toString().toLowerCase();
      
      if (sortDirection === 'asc') {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });
  }, [filteredData, sortField, sortDirection]);

  const handleSort = (fieldKey) => {
    if (sortField === fieldKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(fieldKey);
      setSortDirection('asc');
    }
  };

  const renderTable = (data, displayFields) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return (
        <div className="data-grid-empty">
          <p>No data available</p>
        </div>
      );
    }

    // Normalize display fields - handle both array of objects and array of strings
    let fields = [];
    if (displayFields && displayFields.length > 0) {
      fields = displayFields.map(field => {
        if (typeof field === 'string') {
          return { key: field, label: field };
        } else if (field && typeof field === 'object') {
          return { 
            key: field.key || field.name || field.field, 
            label: field.label || field.name || field.key || field.field 
          };
        }
        return { key: field, label: field };
      });
    } else {
      // Auto-generate fields from first row
      const firstRow = data[0] || {};
      fields = Object.keys(firstRow).map(key => ({ key, label: key }));
    }

    if (fields.length === 0) {
      return (
        <div className="data-grid-empty">
          <p>No fields configured</p>
        </div>
      );
    }
  const totalCols = fields.length;
    const displayData = data.slice(0, 100); // Show up to 100 rows

    return (
      <div>
        {/* Filter Controls */}
        <div className="data-grid-controls">
          <input
            type="text"
            placeholder="Filter data..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="data-grid-filter"
          />
          <span className="data-grid-info">
            {data.length} rows {filterText && `(${data.length} filtered)`}
          </span>
          {(sortField || filterText) && (
            <button
              onClick={() => {
                setSortField('');
                setSortDirection('asc');
                setFilterText('');
              }}
              className="data-grid-clear-btn"
            >
              Clear
            </button>
          )}
        </div>

        {/* Table */}
        <div className="data-grid-container">
          
          <table className="data-grid-table">
            <colgroup>
                {fields.map((field, idx) => (
                  <col
                    key={idx}
                    // If you later add field.width (e.g., '220px' or '20%'), it'll be used.
                    style={{ width: field.width || `${100 / totalCols}%` }}
                  />
                ))}
              </colgroup>
            <thead className="data-grid-thead">
              <tr>
                {fields.map((field, index) => {
                  const fieldKey = field.key;
                  const isActive = sortField === fieldKey;
                  return (
                    <th 
                      key={index} 
                      onClick={() => handleSort(fieldKey)}
                      className={`data-grid-th ${isActive ? 'active' : ''}`}
                    >
                      <div className="data-grid-th-content">
                        <span>{field.label}</span>
                        <span className="data-grid-sort-icon">
                          {isActive ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {displayData.map((row, rowIndex) => (
                <tr key={rowIndex} className="data-grid-row">
                  {fields.map((field, colIndex) => {
                    const fieldKey = field.key;
                    const value = row[fieldKey];
                    return (
                      <td key={colIndex} className="data-grid-td">
                        {value !== null && value !== undefined ? value.toString() : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {data.length > 100 && (
            <div className="data-grid-pagination">
              Showing first 100 of {data.length} rows
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div 
      ref={(node) => drag(drop(node))}
      className={`kpi-card ${isDragging ? 'dragging' : ''}`}
      style={{ 
        gridColumn: 'span 2', 
        minHeight: '400px',
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        transform: isDragging ? 'rotate(2deg)' : 'none',
        transition: 'transform 0.2s ease'
      }}
    >
      <div className="kpi-card-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FiMove style={{ marginRight: '8px', color: '#666', cursor: 'grab' }} size={14} />
          <h3 className="kpi-card-title">{dataGrid.name}</h3>
        </div>
        <div className="kpi-card-actions">
          <button
            onClick={() => onEdit(dataGrid)}
            className="btn btn-small btn-secondary"
            title="Edit Data Grid Name"
            style={{ padding: '4px 8px', marginRight: '5px' }}
          >
            <FiEdit2 size={12} />
          </button>
          <button
            onClick={() => onDelete(dataGrid.dataGridId)}
            className="btn btn-small btn-danger"
            title="Remove Data Grid"
            style={{ padding: '4px 8px' }}
          >
            <FiTrash2 size={12} />
          </button>
        </div>
      </div>
      
      {dataGrid.error ? (
        <div style={{ color: '#dc3545', padding: '20px', textAlign: 'center' }}>
          <p>Error loading data</p>
          <small>{dataGrid.error}</small>
        </div>
      ) : (
        <div style={{ flex: 1 }}>
          {renderTable(sortedData, dataGrid.displayFields)}
        </div>
      )}
      
      <div style={{ marginTop: '15px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
        Last updated: {new Date().toLocaleTimeString()}
      </div>
    </div>
  );
};

const AddDataGridModal = ({ availableDataGrids, userDataGrids, onClose, onAddDataGrid }) => {
  const [selectedDataGrid, setSelectedDataGrid] = useState('');
  const [customName, setCustomName] = useState('');
  const [adding, setAdding] = useState(false);

  // Filter out Data Grids that are already added to the dashboard
  const userDataGridConfigIds = userDataGrids.map(grid => grid.dataGridConfigId);
  const availableToAdd = availableDataGrids.filter(grid => !userDataGridConfigIds.includes(grid.id));

  // Update custom name when Data Grid selection changes
  const handleDataGridSelection = (dataGridId) => {
    setSelectedDataGrid(dataGridId);
    if (dataGridId) {
      const dataGrid = availableToAdd.find(g => g.id.toString() === dataGridId);
      setCustomName(dataGrid ? dataGrid.name : '');
    } else {
      setCustomName('');
    }
  };

  const handleAdd = async () => {
    if (!selectedDataGrid || !customName.trim()) return;

    setAdding(true);
    try {
      await onAddDataGrid(parseInt(selectedDataGrid), customName.trim());
    } catch (error) {
      console.error('Failed to add Data Grid:', error);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>Add Data Grid to Dashboard</h3>
          <button onClick={onClose} className="btn btn-small btn-secondary">×</button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <p style={{ color: '#666', marginBottom: '15px' }}>
            Select a Data Grid configuration to add to your dashboard:
          </p>

          {availableToAdd.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              <p>All available Data Grids have been added to your dashboard.</p>
            </div>
          ) : (
            <div>
              <div className="form-group">
                <label className="form-label">Available Data Grids</label>
                <select
                  value={selectedDataGrid}
                  onChange={(e) => handleDataGridSelection(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select a Data Grid to add</option>
                  {availableToAdd.map(grid => (
                    <option key={grid.id} value={grid.id}>
                      {grid.name} - {grid.endpointName}
                    </option>
                  ))}
                </select>
              </div>

              {selectedDataGrid && (
                <div className="form-group">
                  <label className="form-label">Custom Name</label>
                  <input
                    type="text"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    className="form-input"
                    placeholder="Enter a custom name for this Data Grid"
                    required
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Customize the display name for this Data Grid on your dashboard
                  </small>
                </div>
              )}

              {selectedDataGrid && (
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #dee2e6', 
                  borderRadius: '5px', 
                  padding: '15px',
                  marginBottom: '15px'
                }}>
                  {(() => {
                    const grid = availableToAdd.find(g => g.id.toString() === selectedDataGrid);
                    return grid ? (
                      <div>
                        <h5 style={{ margin: '0 0 10px 0', color: '#333' }}>{grid.name}</h5>
                        <p style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>
                          {grid.description}
                        </p>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          <strong>Data Source:</strong> {grid.endpointName}<br/>
                          <strong>Default Size:</strong> {grid.defaultSize}<br/>
                          <strong>Display Fields:</strong> {grid.displayFields?.length || 0} configured
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          {availableToAdd.length > 0 && (
            <button 
              onClick={handleAdd} 
              disabled={!selectedDataGrid || adding}
              className="btn btn-primary"
            >
              {adding ? 'Adding...' : 'Add Data Grid'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const AddActionModal = ({ availableActions, userActions, onClose, onAddAction }) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [customName, setCustomName] = useState('');
  const [adding, setAdding] = useState(false);

  // Filter out Actions that are already added to the dashboard
  const userActionConfigIds = userActions.map(action => action.actionConfigId);
  const availableToAdd = availableActions.filter(action => !userActionConfigIds.includes(action.id));

  // Update custom name when Action selection changes
  const handleActionSelection = (actionId) => {
    setSelectedAction(actionId);
    if (actionId) {
      const action = availableToAdd.find(a => a.id.toString() === actionId);
      setCustomName(action ? action.name : '');
    } else {
      setCustomName('');
    }
  };

  const handleAdd = async () => {
    if (!selectedAction || !customName.trim()) return;

    setAdding(true);
    try {
      await onAddAction(parseInt(selectedAction), customName.trim());
    } catch (error) {
      console.error('Failed to add Action:', error);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>Add Action to Dashboard</h3>
          <button onClick={onClose} className="btn btn-small btn-secondary">×</button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <p style={{ color: '#666', marginBottom: '15px' }}>
            Select an Action configuration to add to your dashboard:
          </p>

          {availableToAdd.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              <p>All available Actions have been added to your dashboard.</p>
            </div>
          ) : (
            <div>
              <div className="form-group">
                <label className="form-label">Available Actions</label>
                <select
                  value={selectedAction}
                  onChange={(e) => handleActionSelection(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select an Action to add</option>
                  {availableToAdd.map(action => (
                    <option key={action.id} value={action.id}>
                      {action.name} - {action.endpointName}
                    </option>
                  ))}
                </select>
              </div>

              {selectedAction && (
                <div className="form-group">
                  <label className="form-label">Custom Name</label>
                  <input
                    type="text"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    className="form-input"
                    placeholder="Enter a custom name for this Action"
                    required
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Customize the display name for this Action on your dashboard
                  </small>
                </div>
              )}

              {selectedAction && (
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #dee2e6', 
                  borderRadius: '5px', 
                  padding: '15px',
                  marginBottom: '15px'
                }}>
                  {(() => {
                    const action = availableToAdd.find(a => a.id.toString() === selectedAction);
                    return action ? (
                      <div>
                        <h5 style={{ margin: '0 0 10px 0', color: '#333' }}>{action.name}</h5>
                        <p style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>
                          {action.description}
                        </p>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          <strong>Data Source:</strong> {action.endpointName}<br/>
                          <strong>Button Text:</strong> {action.buttonText}<br/>
                          <strong>Form Fields:</strong> {action.formFields?.length || 0} configured<br/>
                          <strong>Status:</strong> {action.isActive ? 'Active' : 'Inactive'}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          {availableToAdd.length > 0 && (
            <button 
              onClick={handleAdd} 
              disabled={!selectedAction || adding}
              className="btn btn-primary"
            >
              {adding ? 'Adding...' : 'Add Action'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const EditDataGridModal = ({ dataGrid, onClose, onUpdate }) => {
  const [customName, setCustomName] = useState(dataGrid.name || '');
  const [updating, setUpdating] = useState(false);

  const handleUpdate = async () => {
    if (!customName.trim()) return;

    setUpdating(true);
    try {
      await onUpdate(dataGrid.dataGridId, customName.trim());
    } catch (error) {
      console.error('Failed to update Data Grid name:', error);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '400px' }}>
        <div className="modal-header">
          <h3>Edit Data Grid Name</h3>
          <button onClick={onClose} className="btn btn-small btn-secondary">×</button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <div className="form-group">
            <label className="form-label">Data Grid Name</label>
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              className="form-input"
              placeholder="Enter Data Grid name"
              required
              autoFocus
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              This name will be displayed on your dashboard
            </small>
          </div>

          <div style={{ 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #dee2e6', 
            borderRadius: '5px', 
            padding: '15px',
            marginTop: '15px'
          }}>
            <div style={{ fontSize: '12px', color: '#666' }}>
              <strong>Size:</strong> {dataGrid.size || 'Large'}<br/>
              <strong>Display Fields:</strong> {dataGrid.displayFields?.length || 0} configured<br/>
              <strong>Data Rows:</strong> {Array.isArray(dataGrid.data) ? dataGrid.data.length : 'Loading...'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button 
            onClick={handleUpdate} 
            disabled={!customName.trim() || updating}
            className="btn btn-primary"
          >
            {updating ? 'Updating...' : 'Update Name'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateGroupModal = ({ onClose, onCreate }) => {
  const [groupName, setGroupName] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!groupName.trim()) return;

    setCreating(true);
    try {
      await onCreate(groupName.trim());
    } catch (error) {
      console.error('Failed to create group:', error);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '400px' }}>
        <div className="modal-header">
          <h3>Create New Group</h3>
          <button onClick={onClose} className="btn btn-small btn-secondary">×</button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <div className="form-group">
            <label className="form-label">Group Name</label>
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              className="form-input"
              placeholder="Enter group name"
              required
              autoFocus
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              Create a new group to organize your KPI cards
            </small>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button 
            onClick={handleCreate} 
            disabled={!groupName.trim() || creating}
            className="btn btn-primary"
          >
            {creating ? 'Creating...' : 'Create Group'}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditGroupModal = ({ groupName, onClose, onUpdate }) => {
  const [newGroupName, setNewGroupName] = useState(groupName || '');
  const [updating, setUpdating] = useState(false);

  const handleUpdate = async () => {
    if (!newGroupName.trim() || newGroupName.trim() === groupName) {
      onClose();
      return;
    }

    setUpdating(true);
    try {
      await onUpdate(groupName, newGroupName.trim());
    } catch (error) {
      console.error('Failed to update group name:', error);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '400px' }}>
        <div className="modal-header">
          <h3>Edit Group Name</h3>
          <button onClick={onClose} className="btn btn-small btn-secondary">×</button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <div className="form-group">
            <label className="form-label">Group Name</label>
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              className="form-input"
              placeholder="Enter group name"
              required
              autoFocus
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              Update the name for this group
            </small>
          </div>

          <div style={{ 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #dee2e6', 
            borderRadius: '5px', 
            padding: '15px',
            marginTop: '15px'
          }}>
            <div style={{ fontSize: '12px', color: '#666' }}>
              <strong>Current Name:</strong> {groupName}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button 
            onClick={handleUpdate} 
            disabled={!newGroupName.trim() || updating}
            className="btn btn-primary"
          >
            {updating ? 'Updating...' : 'Update Name'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Action Card Component
const ActionCard = ({ action }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Debug: Log action data to understand structure
  useEffect(() => {
    console.log('ActionCard received action:', action);
    console.log('Action formFields:', action.formFields);
    console.log('FormFields length:', action.formFields?.length);
  }, [action]);

  const handleExecute = async () => {
    // Only validate if we have form fields and they should be filled
    if (action.formFields && action.formFields.length > 0) {
      // Validate required fields
      const requiredFields = action.formFields.filter(field => field.required);
      for (const field of requiredFields) {
        if (!formData[field.name] || formData[field.name].toString().trim() === '') {
          setError(`${field.label} is required`);
          return;
        }
      }

      // Validate field patterns
      for (const field of action.formFields) {
        if (formData[field.name] && field.pattern) {
          const regex = new RegExp(field.pattern);
          if (!regex.test(formData[field.name])) {
            setError(`${field.label} format is invalid`);
            return;
          }
        }
      }
    }

    // Show confirmation if configured
    if (action.confirmationMessage) {
      if (!window.confirm(action.confirmationMessage)) {
        return;
      }
    }

    setExecuting(true);
    setError('');
    setResult(null);

    try {
      const response = await dashboardAPI.executeAction(action.actionConfigId, formData);
      setResult({
        success: true,
        message: response.data.message,
        data: response.data.result
      });
      setShowForm(false);
      setFormData({});
    } catch (error) {
      console.error('Action execution failed:', error);
      setResult({
        success: false,
        message: error.response?.data?.error || 'Action execution failed',
        details: error.response?.data?.details
      });
    } finally {
      setExecuting(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    setError('');
  };

  const renderFormField = (field) => {
    const value = formData[field.name] || '';
    
    const fieldProps = {
      value,
      onChange: (e) => handleFieldChange(field.name, e.target.value),
      placeholder: field.placeholder || '',
      required: field.required,
      className: 'form-input',
      style: { marginBottom: '10px' }
    };

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            {...fieldProps}
            rows={3}
          />
        );
      case 'email':
        return (
          <input
            {...fieldProps}
            type="email"
          />
        );
      case 'number':
        return (
          <input
            {...fieldProps}
            type="number"
          />
        );
      case 'tel':
        return (
          <input
            {...fieldProps}
            type="tel"
          />
        );
      case 'url':
        return (
          <input
            {...fieldProps}
            type="url"
          />
        );
      default:
        return (
          <input
            {...fieldProps}
            type="text"
          />
        );
    }
  };

  return (
    <div className="kpi-card" style={{ minHeight: '200px' }}>
      <div className="kpi-card-header">
        <h3 className="kpi-card-title">{action.name}</h3>
      </div>
      
      <div style={{ padding: '15px' }}>
        {action.description && (
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '15px' }}>
            {action.description}
          </p>
        )}

        {!showForm && !result && (
          <button
            onClick={() => {
              // Check if form fields exist before showing form
              if (action.formFields && action.formFields.length > 0) {
                setShowForm(true);
              } else {
                // No form fields, execute directly
                handleExecute();
              }
            }}
            className="btn btn-primary"
            style={{ width: '100%' }}
          >
            {action.buttonText || 'Execute'}
          </button>
        )}

        {showForm && action.formFields && action.formFields.length > 0 && (
          <div>
            <h4 style={{ marginBottom: '15px', color: '--text-secondary' }}>Fill out the form:</h4>
            
            {action.formFields.map((field) => (
              <div key={field.name} style={{ marginBottom: '15px' }}>
                <label className="form-label" style={{ 
                  display: 'block', 
                  marginBottom: '5px', 
                  fontWeight: 'bold'
                }}>
                  {field.label}
                  {field.required && <span style={{ color: '#dc3545' }}>*</span>}
                </label>
                {renderFormField(field)}
              </div>
            ))}

            {error && (
              <div style={{ 
                color: '#dc3545', 
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '4px',
                padding: '10px',
                marginBottom: '15px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleExecute}
                disabled={executing}
                className="btn btn-primary"
                style={{ flex: 1 }}
              >
                {executing ? 'Executing...' : action.buttonText}
              </button>
              <button
                onClick={() => {
                  setShowForm(false);
                  setFormData({});
                  setError('');
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {result && (
          <div>
            <div style={{ 
              color: result.success ? '#155724' : '#721c24',
              backgroundColor: result.success ? '#d4edda' : '#f8d7da',
              border: `1px solid ${result.success ? '#c3e6cb' : '#f5c6cb'}`,
              borderRadius: '4px',
              padding: '15px',
              marginBottom: '15px'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                {result.success ? '✓ Success' : '✗ Error'}
              </div>
              <div style={{ fontSize: '14px' }}>
                {result.message}
              </div>
              {result.details && (
                <div style={{ fontSize: '12px', marginTop: '5px', opacity: 0.8 }}>
                  {result.details}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => {
                  setShowForm(true);
                  setResult(null);
                }}
                className="btn btn-primary"
                style={{ flex: 1 }}
              >
                Execute Again
              </button>
              <button
                onClick={() => {
                  setResult(null);
                  setFormData({});
                }}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
      
      <div style={{ 
        padding: '10px 15px', 
        fontSize: '12px', 
        color: '#666', 
        borderTop: '1px solid #eee',
        backgroundColor: '#f8f9fa'
      }}>
        <strong>Endpoint:</strong> {action.endpointName}
      </div>
    </div>
  );
};

export default Dashboard;
